import { useState } from 'react'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { ChevronDown, Check, X, ExternalLink, Loader2, Gauge, Sparkles, Search, MousePointerClick, Eye, TrendingUp, ArrowUpRight, BarChart3, Users, Activity, ArrowDownRight, Globe, Copy, Monitor, Smartphone } from 'lucide-react'
import { usePageDetail, usePageSpeed, useCruxData, type CrawlPage, type LinkRecord } from '../hooks/useCrawls'
import { useMetaSuggestions, type MetaSuggestionItem } from '../hooks/useSuggestions'
import { useGscStatus, useGscPage, useGscInspection, type GscSiteMatch } from '../hooks/useGsc'
import { useGa4Status, useGa4Page, type Ga4PropertyMatch } from '../hooks/useGa4'
import { SerpPreview } from './SerpPreview'
import { SocialPreview } from './SocialPreview'
import { SchemaValidation } from './SchemaValidation'
import { SeoScoreBadge } from './SeoScoreBadge'
import { decodeHtmlEntities } from '@/client/lib/decode'
import { scoreColor } from '@/client/lib/colors'

/** Collapsible section wrapper */
function Section({
  title,
  badge,
  defaultOpen = true,
  children,
}: {
  title: string
  badge?: string
  defaultOpen?: boolean
  children: React.ReactNode
}) {
  const [open, setOpen] = useState(defaultOpen)
  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <CollapsibleTrigger className="flex w-full items-center justify-between py-2 text-sm font-medium hover:text-foreground/80 transition-colors">
        <span className="flex items-center gap-2">
          <ChevronDown
            className={`h-4 w-4 transition-transform ${open ? '' : '-rotate-90'}`}
          />
          {title}
          {badge && (
            <span className="text-xs text-muted-foreground font-normal">({badge})</span>
          )}
        </span>
      </CollapsibleTrigger>
      <CollapsibleContent className="pb-3">
        {children}
      </CollapsibleContent>
    </Collapsible>
  )
}

function BoolBadge({ value, label }: { value: boolean; label: string }) {
  return value ? (
    <span className="inline-flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
      <Check className="h-3 w-3" /> {label}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
      <X className="h-3 w-3" /> {label}
    </span>
  )
}

function DetailRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-2 text-xs py-0.5">
      <span className="w-28 shrink-0 text-muted-foreground">{label}</span>
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  )
}

function SeoEssentials({ page }: { page: CrawlPage }) {
  return (
    <Section title="SEO Essentials">
      <div className="space-y-1">
        <DetailRow label="Title">
          <span className="break-words">
            {page.title || <span className="text-destructive">Missing</span>}
          </span>
          {page.title_length > 0 && (
            <span className={`ml-1 ${page.title_length > 60 ? 'text-yellow-600 dark:text-yellow-400' : 'text-muted-foreground'}`}>
              ({page.title_length} chars)
            </span>
          )}
        </DetailRow>
        <DetailRow label="Description">
          <span className="break-words">
            {page.meta_description || <span className="text-destructive">Missing</span>}
          </span>
          {page.meta_description_length > 0 && (
            <span className={`ml-1 ${page.meta_description_length > 160 ? 'text-yellow-600 dark:text-yellow-400' : 'text-muted-foreground'}`}>
              ({page.meta_description_length} chars)
            </span>
          )}
        </DetailRow>
        <DetailRow label="Canonical">
          {page.canonical_status === 'self' ? (
            <span className="text-green-600 dark:text-green-400">self-referencing</span>
          ) : page.canonical_status === 'different' ? (
            <span className="text-yellow-600 dark:text-yellow-400 break-all">{page.canonical_url}</span>
          ) : (
            <span className="text-destructive">missing</span>
          )}
        </DetailRow>
        <DetailRow label="H1">
          {page.h1_count === 0 ? (
            <span className="text-destructive">Missing</span>
          ) : (
            <>
              {tryParseJson<string[]>(page.h1_texts)?.join(', ') || '-'}
              {page.h1_count > 1 && (
                <span className="text-yellow-600 dark:text-yellow-400 ml-1">({page.h1_count} H1s)</span>
              )}
            </>
          )}
        </DetailRow>
        <div className="flex gap-6 pt-1">
          <DetailRow label="Words">
            <span className={page.word_count < 200 && page.word_count > 0 ? 'text-yellow-600 dark:text-yellow-400' : ''}>
              {page.word_count}
            </span>
          </DetailRow>
          <DetailRow label="Response">
            <span className={page.response_time_ms > 2000 ? 'text-destructive' : page.response_time_ms > 1000 ? 'text-yellow-600 dark:text-yellow-400' : ''}>
              {page.response_time_ms}ms
            </span>
          </DetailRow>
        </div>
        {page.readability_score != null && page.readability_score > 0 && (
          <DetailRow label="Readability">
            <span className={scoreColor(page.readability_score, { good: 60, ok: 30 })}>
              {Math.round(page.readability_score)} — {page.readability_score >= 80 ? 'Very easy' : page.readability_score >= 60 ? 'Easy' : page.readability_score >= 40 ? 'Moderate' : page.readability_score >= 20 ? 'Difficult' : 'Very difficult'}
            </span>
          </DetailRow>
        )}
        {page.depth != null && (
          <DetailRow label="Crawl Depth">
            <span className={page.depth <= 2 ? 'text-green-600 dark:text-green-400' : page.depth <= 3 ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'}>
              {page.depth} {page.depth === 0 ? '(start page)' : page.depth === 1 ? 'click from start' : 'clicks from start'}
            </span>
          </DetailRow>
        )}
        {page.inrank != null && (
          <DetailRow label="InRank">
            <span className={scoreColor(page.inrank, { good: 7, ok: 3 })}>
              {Math.round(page.inrank * 10) / 10}/10
            </span>
            <span className="text-muted-foreground ml-1">
              — {page.inrank >= 7 ? 'High authority' : page.inrank >= 3 ? 'Medium authority' : 'Low authority'}
            </span>
          </DetailRow>
        )}
        {page.meta_keywords && (
          <DetailRow label="Meta Keywords">
            <span className="text-muted-foreground break-words">{page.meta_keywords}</span>
          </DetailRow>
        )}
        {(page.pagination_next || page.pagination_prev) && (
          <DetailRow label="Pagination">
            <span className="text-xs font-mono break-all">
              {page.pagination_prev && <span>{'\u2190'} {page.pagination_prev}</span>}
              {page.pagination_prev && page.pagination_next && <span className="mx-1">|</span>}
              {page.pagination_next && <span>{page.pagination_next} {'\u2192'}</span>}
            </span>
          </DetailRow>
        )}
      </div>
    </Section>
  )
}

function HeadingsSection({ page }: { page: CrawlPage }) {
  const counts = [
    { level: 'H1', count: page.h1_count },
    { level: 'H2', count: page.h2_count },
    { level: 'H3', count: page.h3_count },
    { level: 'H4', count: page.h4_count },
    { level: 'H5', count: page.h5_count },
    { level: 'H6', count: page.h6_count },
  ].filter((h) => h.count > 0)

  const summary = counts.map((h) => `${h.level}\u00d7${h.count}`).join(', ')

  return (
    <Section title="Headings" badge={summary || 'none'}>
      {counts.length === 0 ? (
        <p className="text-xs text-muted-foreground">No headings found.</p>
      ) : (
        <div className="space-y-1">
          {page.h1_count > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground font-medium">H1:</span>{' '}
              {tryParseJson<string[]>(page.h1_texts)?.join(' | ') || '-'}
            </div>
          )}
          {page.h2_count > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground font-medium">H2:</span>{' '}
              {tryParseJson<string[]>(page.h2_texts)?.join(' | ') || `${page.h2_count} found`}
            </div>
          )}
          {page.h3_count > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground font-medium">H3:</span>{' '}
              {tryParseJson<string[]>(page.h3_texts)?.join(' | ') || `${page.h3_count} found`}
            </div>
          )}
          {!page.heading_hierarchy_valid && (
            <p className="text-xs text-yellow-600 dark:text-yellow-400">
              Non-sequential heading hierarchy detected
            </p>
          )}
        </div>
      )}
    </Section>
  )
}

function InlinksSection({ inlinks }: { inlinks: LinkRecord[] }) {
  return (
    <Section title="Inlinks" badge={String(inlinks.length)}>
      {inlinks.length === 0 ? (
        <p className="text-xs text-muted-foreground">No internal pages link to this URL (orphan page).</p>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-1">
          {inlinks.map((link, i) => (
            <div key={i} className="flex items-baseline gap-2 text-xs">
              <span className="font-mono truncate min-w-0 flex-1" title={link.source_url}>
                {link.source_url}
              </span>
              {link.anchor_text && (
                <span className="shrink-0 text-muted-foreground truncate max-w-[150px]" title={link.anchor_text}>
                  &rarr; &quot;{link.anchor_text}&quot;
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}

function OutlinksSection({ outlinks }: { outlinks: LinkRecord[] }) {
  return (
    <Section title="Outlinks" badge={String(outlinks.length)}>
      {outlinks.length === 0 ? (
        <p className="text-xs text-muted-foreground">This page has no outgoing links.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-1">
          {outlinks.map((link, i) => (
            <div key={i} className="flex items-center gap-2 text-xs">
              <span className="font-mono truncate min-w-0 flex-1" title={link.target_url}>
                {link.target_url}
              </span>
              <Badge variant="outline" className="shrink-0 text-[10px] px-1 py-0">
                {link.link_type}
              </Badge>
              {link.status_code ? (
                <Badge
                  variant={link.status_code >= 400 ? 'destructive' : 'secondary'}
                  className="shrink-0 text-[10px] px-1 py-0"
                >
                  {link.status_code}
                </Badge>
              ) : (
                <span className="shrink-0 text-muted-foreground text-[10px]">-</span>
              )}
            </div>
          ))}
        </div>
      )}
    </Section>
  )
}

/** Extract a readable filename from a URL or src string */
function imgFilename(src: string): string {
  try {
    const pathname = new URL(src, 'https://x').pathname
    const last = pathname.split('/').pop() || ''
    // Decode URI and truncate query params
    return decodeURIComponent(last.split('?')[0] ?? last) || src.slice(0, 40)
  } catch {
    return src.slice(0, 40) || '(unknown)'
  }
}

function ImagesSection({ page }: { page: CrawlPage }) {
  const images = tryParseJson<Array<{ src: string; alt: string | null }>>(page.images_data) || []
  const missingAlt = page.images_missing_alt

  return (
    <Section title="Images" badge={`${page.images_count} total${missingAlt > 0 ? `, ${missingAlt} missing alt` : ''}`}>
      {images.length === 0 ? (
        <p className="text-xs text-muted-foreground">
          {page.images_count > 0 ? `${page.images_count} images detected.` : 'No images found.'}
        </p>
      ) : (
        <div className="max-h-48 overflow-y-auto space-y-1">
          {images.map((img, i) => {
            const filename = imgFilename(img.src)
            return (
              <div key={i} className="grid grid-cols-[16px_1fr_1fr] items-center gap-2 text-xs">
                {img.alt ? (
                  <Check className="h-3 w-3 text-green-500" />
                ) : (
                  <X className="h-3 w-3 text-destructive" />
                )}
                <span className="font-mono truncate min-w-0" title={img.src}>
                  {filename}
                </span>
                <span className={`truncate min-w-0 ${img.alt ? 'text-muted-foreground' : 'text-destructive'}`} title={img.alt ? decodeHtmlEntities(img.alt) : '(no alt)'}>
                  {img.alt ? `"${decodeHtmlEntities(img.alt)}"` : '(no alt)'}
                </span>
              </div>
            )
          })}
        </div>
      )}
    </Section>
  )
}

function ResourcesSection({ page }: { page: CrawlPage }) {
  const resources = tryParseJson<Array<{ type: string; src: string; async?: boolean; defer?: boolean; module?: boolean }>>(
    page.resources_data
  ) || []

  if (resources.length === 0) {
    return (
      <Section title="Resources" badge="none" defaultOpen={false}>
        <p className="text-xs text-muted-foreground">No CSS/JS resources detected.</p>
      </Section>
    )
  }

  const scripts = resources.filter((r) => r.type === 'script')
  const stylesheets = resources.filter((r) => r.type === 'stylesheet')

  return (
    <Section title="Resources" badge={`${scripts.length} JS, ${stylesheets.length} CSS`} defaultOpen={false}>
      <div className="max-h-48 overflow-y-auto space-y-1">
        {resources.map((r, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            <Badge variant="outline" className="shrink-0 text-[10px] px-1 py-0 w-[50px] justify-center">
              {r.type === 'script' ? 'JS' : r.type === 'stylesheet' ? 'CSS' : 'Pre'}
            </Badge>
            <span className="font-mono truncate min-w-0 flex-1" title={r.src}>
              {r.src}
            </span>
            {r.async && <Badge variant="secondary" className="shrink-0 text-[10px] px-1 py-0">async</Badge>}
            {r.defer && <Badge variant="secondary" className="shrink-0 text-[10px] px-1 py-0">defer</Badge>}
            {r.module && <Badge variant="secondary" className="shrink-0 text-[10px] px-1 py-0">module</Badge>}
          </div>
        ))}
      </div>
    </Section>
  )
}

function ExtractionResultsSection({ page }: { page: CrawlPage }) {
  const extractions = tryParseJson<Record<string, string[]>>(page.custom_extractions)
  if (!extractions || Object.keys(extractions).length === 0) return null

  return (
    <Section title="Custom Extractions" badge={`${Object.keys(extractions).length} rules`}>
      <div className="space-y-3 text-xs">
        {Object.entries(extractions).map(([label, values]) => (
          <div key={label}>
            <span className="font-medium text-muted-foreground">{label}</span>
            {values.length === 0 ? (
              <p className="text-muted-foreground italic mt-0.5">No matches</p>
            ) : (
              <ul className="mt-0.5 space-y-0.5">
                {values.map((v, i) => (
                  <li key={i} className="font-mono break-all">{v}</li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </Section>
  )
}

/** AI Page Audit result shape (from Browser Rendering /json) */
interface AiAuditData {
  page_purpose?: string
  target_audience?: string
  first_impression?: string
  content_quality?: string
  trust_signals?: string[]
  cta_analysis?: string
  visual_hierarchy?: string
  ux_issues?: string[]
  mobile_readiness?: string
  scores?: {
    first_impression?: number
    content_quality?: number
    conversion_potential?: number
  }
  top_recommendations?: string[]
}

function AuditScoreBadge({ label, score }: { label: string; score: number | undefined }) {
  if (score == null) return null
  const color = `${scoreColor(score, { good: 8, ok: 6 })} ${score >= 8 ? 'border-green-600/30' : score >= 6 ? 'border-yellow-600/30' : 'border-red-600/30'}`
  return (
    <div className={`flex flex-col items-center rounded-md border px-3 py-2 ${color}`}>
      <span className="text-lg font-bold">{score}</span>
      <span className="text-[10px] text-muted-foreground">{label}</span>
    </div>
  )
}

function ReadableContentSection({ page }: { page: CrawlPage }) {
  if (!page.markdown_content) return null

  return (
    <Section title="Readable Content" badge={`${(page.markdown_content.length / 1024).toFixed(1)}KB`} defaultOpen={false}>
      <div className="max-h-80 overflow-y-auto rounded border bg-muted/30 p-3">
        <pre className="text-xs whitespace-pre-wrap font-sans leading-relaxed">
          {page.markdown_content}
        </pre>
      </div>
    </Section>
  )
}

function AiPageAuditSection({ page }: { page: CrawlPage }) {
  const audit = tryParseJson<AiAuditData>(page.ai_page_audit)
  if (!audit) return null

  return (
    <Section title="AI Page Audit" defaultOpen={true}>
      <div className="space-y-3 text-xs">
        {/* Scores row */}
        {audit.scores && (
          <div className="grid grid-cols-3 gap-2">
            <AuditScoreBadge label="First Impression" score={audit.scores.first_impression} />
            <AuditScoreBadge label="Content Quality" score={audit.scores.content_quality} />
            <AuditScoreBadge label="Conversion" score={audit.scores.conversion_potential} />
          </div>
        )}

        {/* Purpose & audience */}
        {audit.page_purpose && (
          <div>
            <span className="font-medium text-muted-foreground">Page Purpose</span>
            <p className="mt-0.5">{audit.page_purpose}</p>
          </div>
        )}
        {audit.target_audience && (
          <div>
            <span className="font-medium text-muted-foreground">Target Audience</span>
            <p className="mt-0.5">{audit.target_audience}</p>
          </div>
        )}

        {/* First impression */}
        {audit.first_impression && (
          <div>
            <span className="font-medium text-muted-foreground">First Impression</span>
            <p className="mt-0.5">{audit.first_impression}</p>
          </div>
        )}

        {/* Content quality */}
        {audit.content_quality && (
          <div>
            <span className="font-medium text-muted-foreground">Content Quality</span>
            <p className="mt-0.5">{audit.content_quality}</p>
          </div>
        )}

        {/* Trust signals */}
        {audit.trust_signals && audit.trust_signals.length > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">Trust Signals</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {audit.trust_signals.map((signal, i) => (
                <Badge key={i} variant="secondary" className="text-[10px]">{signal}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA analysis */}
        {audit.cta_analysis && (
          <div>
            <span className="font-medium text-muted-foreground">Call to Action</span>
            <p className="mt-0.5">{audit.cta_analysis}</p>
          </div>
        )}

        {/* UX issues */}
        {audit.ux_issues && audit.ux_issues.length > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">UX Issues</span>
            <ul className="mt-0.5 space-y-0.5">
              {audit.ux_issues.map((issue, i) => (
                <li key={i} className="flex items-start gap-1.5 text-yellow-600 dark:text-yellow-400">
                  <span className="shrink-0 mt-0.5">!</span>
                  <span>{issue}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Top recommendations */}
        {audit.top_recommendations && audit.top_recommendations.length > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">Top Recommendations</span>
            <ul className="mt-0.5 space-y-0.5 list-disc list-inside">
              {audit.top_recommendations.map((rec, i) => (
                <li key={i}>{rec}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Section>
  )
}

function ScreenshotSection({ crawlId, page }: { crawlId: string; page: CrawlPage }) {
  const [device, setDevice] = useState<'desktop' | 'mobile'>('desktop')
  const hasMobile = !!page.screenshot_mobile_key
  const deviceParam = device === 'mobile' ? '&device=mobile' : ''

  return (
    <Section title="Page Screenshot">
      {hasMobile && (
        <div className="flex gap-1 mb-2">
          <Button
            variant={device === 'desktop' ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setDevice('desktop')}
          >
            <Monitor className="h-3 w-3 mr-1" />
            Desktop
          </Button>
          <Button
            variant={device === 'mobile' ? 'default' : 'outline'}
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => setDevice('mobile')}
          >
            <Smartphone className="h-3 w-3 mr-1" />
            Mobile
          </Button>
        </div>
      )}
      <img
        key={device}
        src={`/api/crawls/${crawlId}/screenshot?url=${encodeURIComponent(page.url)}${deviceParam}`}
        alt={`${device === 'mobile' ? 'Mobile' : 'Desktop'} screenshot of ${page.url}`}
        loading="lazy"
        className={`rounded border cursor-pointer hover:opacity-90 transition-opacity ${device === 'mobile' ? 'max-w-[375px] mx-auto' : 'w-full'}`}
        onClick={() => window.open(`/api/crawls/${crawlId}/screenshot?url=${encodeURIComponent(page.url)}${deviceParam}`, '_blank')}
      />
    </Section>
  )
}

/** AI Analysis v2 result shape (from ai_analysis JSON column) */
interface AiAnalysisData {
  pageType?: string
  primaryTopic?: string
  targetAudience?: string
  issues?: Array<{
    type: string
    severity: 'error' | 'warning' | 'info'
    category: string
    title: string
    description: string
    element?: string
    recommendation: string
  }>
  spellingIssues?: Array<{
    word: string
    suggestions: string[]
    count: number
    severity: 'error' | 'suggestion'
    context?: string
  }>
  scores?: {
    firstImpression?: number
    contentQuality?: number
    conversionPotential?: number
    seoEffectiveness?: number
    accessibility?: number
  }
  trustSignals?: string[]
  hasCta?: boolean
  ctaStrength?: string
}

function AiAnalysisSection({ page }: { page: CrawlPage }) {
  const raw = tryParseJson<AiAnalysisData & { type?: string }>(page.ai_analysis)
  if (!raw) return null
  // Normalise: AI may return "type" instead of "pageType"
  const analysis = { ...raw, pageType: raw.pageType || raw.type }

  const severityColor = (s: string) =>
    s === 'error' ? 'text-red-600 dark:text-red-400 border-red-600/30 bg-red-50 dark:bg-red-950/30'
    : s === 'warning' ? 'text-yellow-600 dark:text-yellow-400 border-yellow-600/30 bg-yellow-50 dark:bg-yellow-950/30'
    : 'text-blue-600 dark:text-blue-400 border-blue-600/30 bg-blue-50 dark:bg-blue-950/30'

  return (
    <Section title="AI Analysis" defaultOpen={true}>
      <div className="space-y-3 text-xs">
        {/* Page classification + scores */}
        <div className="flex items-center gap-2 flex-wrap">
          {analysis.pageType && (
            <Badge variant="outline" className="capitalize">{analysis.pageType.replace(/_/g, ' ')}</Badge>
          )}
          {analysis.primaryTopic && (
            <span className="text-muted-foreground">{analysis.primaryTopic}</span>
          )}
        </div>

        {/* 5 score badges */}
        {analysis.scores && (
          <div className="grid grid-cols-5 gap-1.5">
            <AuditScoreBadge label="First Impression" score={analysis.scores.firstImpression} />
            <AuditScoreBadge label="Content" score={analysis.scores.contentQuality} />
            <AuditScoreBadge label="Conversion" score={analysis.scores.conversionPotential} />
            <AuditScoreBadge label="SEO" score={analysis.scores.seoEffectiveness} />
            <AuditScoreBadge label="A11y" score={analysis.scores.accessibility} />
          </div>
        )}

        {/* Trust signals */}
        {analysis.trustSignals && analysis.trustSignals.length > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">Trust Signals</span>
            <div className="mt-1 flex flex-wrap gap-1">
              {analysis.trustSignals.map((signal, i) => (
                <Badge key={i} variant="secondary" className="text-[10px]">{signal}</Badge>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        {analysis.ctaStrength && (
          <div className="flex items-center gap-2">
            <span className="font-medium text-muted-foreground">CTA:</span>
            <Badge variant={analysis.ctaStrength === 'strong' ? 'default' : analysis.ctaStrength === 'weak' ? 'secondary' : 'outline'}>
              {analysis.ctaStrength}
            </Badge>
          </div>
        )}

        {/* AI-detected issues */}
        {analysis.issues && analysis.issues.length > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">Issues ({analysis.issues.length})</span>
            <div className="mt-1.5 space-y-2">
              {analysis.issues.map((issue, i) => (
                <div key={i} className={`rounded border p-2 ${severityColor(issue.severity)}`}>
                  <div className="flex items-center gap-1.5 font-medium">
                    <Badge variant="outline" className="text-[9px] px-1 py-0 uppercase">
                      {issue.severity}
                    </Badge>
                    {issue.title}
                  </div>
                  <p className="mt-0.5 text-foreground/80">{issue.description}</p>
                  {issue.element && (
                    <p className="mt-0.5 font-mono text-[10px] text-muted-foreground truncate" title={issue.element}>
                      {issue.element}
                    </p>
                  )}
                  <p className="mt-1 text-foreground/70"><span className="font-medium">Fix:</span> {issue.recommendation}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Spelling issues from AI */}
        {analysis.spellingIssues && analysis.spellingIssues.length > 0 && (
          <div>
            <span className="font-medium text-muted-foreground">Spelling ({analysis.spellingIssues.length})</span>
            <div className="mt-1 space-y-1">
              {analysis.spellingIssues.slice(0, 10).map((s, i) => (
                <div key={i} className="flex items-center gap-2">
                  <span className={`font-mono ${s.severity === 'error' ? 'text-red-600 dark:text-red-400' : 'text-blue-600 dark:text-blue-400'}`}>
                    {s.word}
                  </span>
                  {s.suggestions.length > 0 && (
                    <span className="text-muted-foreground">&rarr; {s.suggestions.join(', ')}</span>
                  )}
                  {s.count > 1 && (
                    <span className="text-muted-foreground">({s.count}x)</span>
                  )}
                </div>
              ))}
              {analysis.spellingIssues.length > 10 && (
                <p className="text-xs text-muted-foreground mt-1">
                  +{analysis.spellingIssues.length - 10} more spelling issues
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </Section>
  )
}

function SpellingsSection({ page }: { page: CrawlPage }) {
  const spellings = tryParseJson<Array<{ word: string; suggestions: string[]; count: number }>>(page.us_spellings)
  if (!spellings || spellings.length === 0) return null

  return (
    <Section title="Spelling Errors" badge={`${spellings.length} words`} defaultOpen={false}>
      <div className="text-xs">
        <div className="grid grid-cols-[1fr_1fr_auto] gap-x-4 gap-y-1">
          <span className="font-medium text-muted-foreground">Misspelled</span>
          <span className="font-medium text-muted-foreground">Suggestions</span>
          <span className="font-medium text-muted-foreground text-right">Count</span>
          {spellings.map((s) => (
            <div key={s.word} className="contents">
              <span className="font-mono text-red-600 dark:text-red-400">{s.word}</span>
              <span className="font-mono text-green-600 dark:text-green-400">
                {s.suggestions?.join(', ') || '—'}
              </span>
              <span className="text-right text-muted-foreground">{s.count}</span>
            </div>
          ))}
        </div>
      </div>
    </Section>
  )
}

function HreflangSection({ page }: { page: CrawlPage }) {
  const tags = tryParseJson<Array<{ lang: string; url: string }>>(page.hreflang_tags)
  if (!tags || tags.length === 0) return null

  return (
    <Section title="Hreflang Annotations" badge={`${tags.length} languages`} defaultOpen={false}>
      <div className="text-xs space-y-1">
        {tags.map((tag, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="font-mono font-medium w-16 shrink-0 text-blue-600 dark:text-blue-400">{tag.lang}</span>
            <span className="truncate text-muted-foreground" title={tag.url}>
              {tag.url === page.url ? (
                <span className="text-green-600 dark:text-green-400">self</span>
              ) : (
                tag.url
              )}
            </span>
          </div>
        ))}
      </div>
    </Section>
  )
}

function RedirectChainSection({ page }: { page: CrawlPage }) {
  const raw = tryParseJson<Array<string | { url: string; status: number }>>(page.redirect_chain)
  if (!raw || raw.length === 0) return null

  // Normalise: old crawls store string[], new crawls store {url, status}[]
  const chain = raw.map(item =>
    typeof item === 'string' ? { url: item, status: 0 } : item
  )

  return (
    <Section title="Redirect Chain" badge={`${chain.length} hops`} defaultOpen={false}>
      <div className="space-y-1 text-xs">
        <div className="flex items-center gap-2">
          <Badge variant="outline" className="shrink-0 text-[10px] px-1 py-0">start</Badge>
          <span className="font-mono truncate min-w-0" title={page.url}>{page.url}</span>
        </div>
        {chain.map((hop, i) => (
          <div key={i} className="flex items-center gap-2 pl-4">
            <span className="text-muted-foreground">&darr;</span>
            <Badge variant="secondary" className="shrink-0 text-[10px] px-1 py-0">
              {hop.status > 0 ? hop.status : (i === chain.length - 1 ? page.status_code : '3xx')}
            </Badge>
            <span className="font-mono truncate min-w-0" title={hop.url}>{hop.url}</span>
          </div>
        ))}
      </div>
    </Section>
  )
}

function ResponseHeadersSection({ page }: { page: CrawlPage }) {
  const headers = tryParseJson<Record<string, string>>(page.response_headers)
  if (!headers || Object.keys(headers).length === 0) return null

  const securityHeaders = ['strict-transport-security', 'content-security-policy', 'x-frame-options']
  const warningHeaders = ['x-powered-by']

  return (
    <Section title="Response Headers" badge={`${Object.keys(headers).length}`} defaultOpen={false}>
      <div className="space-y-1">
        {Object.entries(headers).map(([key, value]) => (
          <div key={key} className="flex items-start gap-2 text-xs">
            <span className="shrink-0 flex items-center gap-1">
              {securityHeaders.includes(key) ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : warningHeaders.includes(key) ? (
                <span className="text-yellow-500 text-[10px]">!</span>
              ) : null}
              <span className="text-muted-foreground font-medium w-44 truncate" title={key}>{key}</span>
            </span>
            <span className="font-mono break-all min-w-0">{value}</span>
          </div>
        ))}
        {!headers['content-security-policy'] && (
          <div className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400 pt-1">
            <X className="h-3 w-3" /> Missing Content-Security-Policy
          </div>
        )}
        {!headers['x-frame-options'] && (
          <div className="flex items-center gap-1 text-xs text-yellow-600 dark:text-yellow-400">
            <X className="h-3 w-3" /> Missing X-Frame-Options
          </div>
        )}
      </div>
    </Section>
  )
}

function TechnicalSection({ page }: { page: CrawlPage }) {
  const robots = tryParseJson<string[]>(page.meta_robots) || []

  return (
    <Section title="Technical" defaultOpen={false}>
      <div className="space-y-2 text-xs">
        <div className="flex flex-wrap gap-3">
          <BoolBadge value={!!page.has_viewport} label="Viewport" />
          <BoolBadge value={!page.mixed_content_count} label={`Mixed content: ${page.mixed_content_count}`} />
        </div>
        {robots.length > 0 && (
          <div>
            <span className="text-muted-foreground font-medium">Robots: </span>
            {robots.join(', ')}
          </div>
        )}
        {page.content_language && (
          <div>
            <span className="text-muted-foreground font-medium">Language: </span>
            {page.content_language}
          </div>
        )}
        {page.redirect_url && (
          <div>
            <span className="text-muted-foreground font-medium">Redirects to: </span>
            <span className="font-mono break-all">{page.redirect_url}</span>
          </div>
        )}
        {page.last_modified && (
          <div>
            <span className="text-muted-foreground font-medium">Last-Modified: </span>
            {page.last_modified}
          </div>
        )}
      </div>
    </Section>
  )
}

function PageSpeedSection({ crawlId, pageUrl }: { crawlId: string; pageUrl: string }) {
  const { data, isLoading, isFetching, refetch } = usePageSpeed(crawlId, pageUrl)

  if (!data && !isLoading) {
    return (
      <Section title="PageSpeed Insights" defaultOpen={false}>
        <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
          {isFetching ? (
            <>
              <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />
              Analysing...
            </>
          ) : (
            <>
              <Gauge className="mr-1.5 h-3.5 w-3.5" />
              Run PageSpeed Test
            </>
          )}
        </Button>
        <p className="text-xs text-muted-foreground mt-2">
          Fetches live performance data from Google PageSpeed Insights API.
        </p>
      </Section>
    )
  }

  if (isFetching && !data) {
    return (
      <Section title="PageSpeed Insights" defaultOpen={false}>
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
      </Section>
    )
  }

  if (!data) return null

  return (
    <Section title="PageSpeed Insights" badge="mobile" defaultOpen={false}>
      <div className="space-y-3 text-xs">
        <div className="grid grid-cols-4 gap-2">
          <ScoreCard label="Performance" score={data.scores.performance} />
          <ScoreCard label="Accessibility" score={data.scores.accessibility} />
          <ScoreCard label="Best Practices" score={data.scores.bestPractices} />
          <ScoreCard label="SEO" score={data.scores.seo} />
        </div>
        <div className="space-y-1.5">
          <p className="font-medium text-muted-foreground">Core Web Vitals</p>
          <div className="grid grid-cols-[1fr_1fr] gap-x-4 gap-y-1">
            <MetricRow label="LCP" metric={data.coreWebVitals.lcp} />
            <MetricRow label="CLS" metric={data.coreWebVitals.cls} />
            <MetricRow label="TBT" metric={data.coreWebVitals.tbt} />
            <MetricRow label="FCP" metric={data.coreWebVitals.fcp} />
            <MetricRow label="SI" metric={data.coreWebVitals.si} />
            <MetricRow label="TTI" metric={data.coreWebVitals.tti} />
          </div>
        </div>
        <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => refetch()}>
          {isFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
          Re-test
        </Button>
      </div>
    </Section>
  )
}

function ScoreCard({ label, score }: { label: string; score: number | null }) {
  if (score == null) return null
  const color = scoreColor(score, { good: 90, ok: 50 })
  return (
    <div className="text-center">
      <p className={`text-lg font-bold ${color}`}>{score}</p>
      <p className="text-muted-foreground leading-tight">{label}</p>
    </div>
  )
}

function MetricRow({ label, metric }: { label: string; metric: { score: number | null; value: string | null } }) {
  if (!metric.value) return null
  const color = metric.score != null ? scoreColor(metric.score, { good: 90, ok: 50 }) : ''
  return (
    <div className="flex justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span className={color}>{metric.value}</span>
    </div>
  )
}

/** CrUX metric colour based on Core Web Vitals thresholds */
function cruxColor(metric: string, value: number | null): string {
  if (value == null) return ''
  const thresholds: Record<string, [number, number]> = {
    lcp: [2500, 4000],
    cls: [0.1, 0.25],
    inp: [200, 500],
    fcp: [1800, 3000],
    ttfb: [800, 1800],
  }
  const t = thresholds[metric]
  if (!t) return ''
  // CWV: lower is better — inverted thresholds, can't use scoreColor directly
  return value <= t[0] ? 'text-green-600 dark:text-green-400' : value <= t[1] ? 'text-yellow-600 dark:text-yellow-400' : 'text-red-600 dark:text-red-400'
}

function cruxFormat(metric: string, value: number | null): string {
  if (value == null) return 'N/A'
  if (metric === 'cls') return value.toFixed(2)
  return `${Math.round(value)} ms`
}

function CruxSection({ crawlId, pageUrl }: { crawlId: string; pageUrl: string }) {
  const urlQuery = useCruxData(crawlId, pageUrl, 'url')
  const originQuery = useCruxData(crawlId, pageUrl, 'origin')
  const data = urlQuery.data || originQuery.data
  const isLoading = urlQuery.isFetching || originQuery.isFetching
  const scope = urlQuery.data ? 'url' : originQuery.data ? 'origin' : null

  if (!data && !isLoading) {
    return (
      <Section title="CrUX Field Data" defaultOpen={false}>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => urlQuery.refetch()} disabled={isLoading}>
            {urlQuery.isFetching ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Load Page Data
          </Button>
          <Button variant="outline" size="sm" onClick={() => originQuery.refetch()} disabled={isLoading}>
            {originQuery.isFetching ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : null}
            Load Origin Data
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-2">
          Real-user performance data from Chrome UX Report (28-day aggregate).
        </p>
      </Section>
    )
  }

  if (isLoading && !data) {
    return (
      <Section title="CrUX Field Data" defaultOpen={false}>
        <div className="space-y-2">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-4 w-48" />
        </div>
      </Section>
    )
  }

  if (!data) return null

  const m = data.metrics
  const metrics: [string, string, number | null][] = [
    ['LCP', 'lcp', m.lcp],
    ['CLS', 'cls', m.cls],
    ['INP', 'inp', m.inp],
    ['FCP', 'fcp', m.fcp],
    ['TTFB', 'ttfb', m.ttfb],
  ]

  return (
    <Section title="CrUX Field Data" badge={scope === 'origin' ? 'origin' : 'page'} defaultOpen={false}>
      <div className="space-y-2 text-xs">
        <div className="space-y-1">
          {metrics.map(([label, key, value]) => (
            <div key={key} className="flex justify-between">
              <span className="text-muted-foreground">{label} (p75)</span>
              <span className={cruxColor(key, value)}>{cruxFormat(key, value)}</span>
            </div>
          ))}
        </div>
        {data.collectionPeriod && (
          <p className="text-muted-foreground">
            {data.collectionPeriod.firstDate.year}-{String(data.collectionPeriod.firstDate.month).padStart(2, '0')}-{String(data.collectionPeriod.firstDate.day).padStart(2, '0')}
            {' to '}
            {data.collectionPeriod.lastDate.year}-{String(data.collectionPeriod.lastDate.month).padStart(2, '0')}-{String(data.collectionPeriod.lastDate.day).padStart(2, '0')}
          </p>
        )}
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => urlQuery.refetch()}>
            {urlQuery.isFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Re-test (page)
          </Button>
          <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => originQuery.refetch()}>
            {originQuery.isFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
            Re-test (origin)
          </Button>
        </div>
      </div>
    </Section>
  )
}

/** Try to parse a JSON string, return null on failure */
function tryParseJson<T>(raw: string | null): T | null {
  if (!raw) return null
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = () => {
    navigator.clipboard.writeText(text)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  return (
    <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={handleCopy} aria-label="Copy to clipboard">
      {copied ? <Check className="h-3 w-3 text-green-600 dark:text-green-400" /> : <Copy className="h-3 w-3" />}
    </Button>
  )
}

function SuggestionCard({ item, type }: { item: MetaSuggestionItem; type: 'title' | 'description' }) {
  const isGoodLength = type === 'title'
    ? item.charCount >= 50 && item.charCount <= 60
    : item.charCount >= 120 && item.charCount <= 155

  return (
    <div className="border rounded px-3 py-2 bg-muted/50 space-y-1">
      <div className="flex items-center justify-between">
        <span className={`text-xs ${isGoodLength ? 'text-green-600 dark:text-green-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
          {item.charCount} chars
        </span>
        <CopyButton text={item.text} />
      </div>
      <p className="text-sm">{item.text}</p>
      <p className="text-[11px] text-muted-foreground italic">{item.reasoning}</p>
    </div>
  )
}

function AiSuggestionsSection({ crawlId, pageId }: { crawlId: string; pageId: string }) {
  const { data, isFetching, refetch, isError } = useMetaSuggestions(crawlId, Number(pageId))

  return (
    <Section title="AI Suggestions" defaultOpen={false}>
      {!data && !isFetching && (
        <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
          <Sparkles className="mr-2 h-3 w-3" />
          Generate Suggestions
        </Button>
      )}
      {isFetching && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Generating suggestions...
        </div>
      )}
      {isError && <p className="text-sm text-destructive">Failed to generate suggestions. Try again.</p>}
      {data && data.available !== false && (
        <div className="space-y-4">
          {(data.titles?.length ?? 0) > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Title Suggestions</h4>
              <div className="space-y-2">
                {data.titles!.map((item, i) => (
                  <SuggestionCard key={i} item={item} type="title" />
                ))}
              </div>
            </div>
          )}
          {(data.descriptions?.length ?? 0) > 0 && (
            <div>
              <h4 className="text-xs font-medium text-muted-foreground mb-2">Description Suggestions</h4>
              <div className="space-y-2">
                {data.descriptions!.map((item, i) => (
                  <SuggestionCard key={i} item={item} type="description" />
                ))}
              </div>
            </div>
          )}
          <Button size="sm" variant="ghost" onClick={() => refetch()} disabled={isFetching}>
            <Sparkles className="mr-2 h-3 w-3" />
            Regenerate
          </Button>
        </div>
      )}
    </Section>
  )
}

function GscSection({ crawlId, pageUrl }: { crawlId: string; pageUrl: string }) {
  const { data: status } = useGscStatus(crawlId)
  const accounts = status?.accounts || []

  if (!status?.connected || accounts.length === 0) return null

  const account = accounts[0]!
  return <GscDataSection crawlId={crawlId} pageUrl={pageUrl} account={account} />
}

function GscDataSection({ crawlId, pageUrl, account }: { crawlId: string; pageUrl: string; account: GscSiteMatch }) {
  const { data: pageData, isFetching: pageFetching, refetch: refetchPage } = useGscPage(crawlId, account.siteUrl, pageUrl)
  const { data: inspectData, isFetching: inspectFetching, refetch: refetchInspect } = useGscInspection(crawlId, account.siteUrl, pageUrl)

  return (
    <Section title="Search Console" defaultOpen={false}>
      <div className="space-y-4 text-xs">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <Search className="h-3 w-3" />
          <span className="truncate">{account.siteUrl}</span>
          <span className="text-[10px]">({account.email})</span>
        </div>

        {/* Page performance */}
        {!pageData && !pageFetching && (
          <Button variant="outline" size="sm" onClick={() => refetchPage()} disabled={pageFetching}>
            <MousePointerClick className="mr-1.5 h-3.5 w-3.5" />
            View GSC Data
          </Button>
        )}
        {pageFetching && !pageData && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading GSC data...
          </div>
        )}
        {pageData && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.clicks.toLocaleString()}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <MousePointerClick className="h-3 w-3" /> Clicks
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.impressions.toLocaleString()}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <Eye className="h-3 w-3" /> Impr
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">{(pageData.totals.ctr * 100).toFixed(1)}%</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <TrendingUp className="h-3 w-3" /> CTR
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.position.toFixed(1)}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <ArrowUpRight className="h-3 w-3" /> Pos
                </p>
              </div>
            </div>

            {pageData.queries.length > 0 && (
              <div>
                <p className="font-medium text-muted-foreground mb-1">Top Queries for This Page</p>
                <div className="max-h-36 overflow-y-auto space-y-0.5">
                  {pageData.queries.slice(0, 10).map((q) => (
                    <div key={q.query} className="flex items-center justify-between py-0.5">
                      <span className="truncate min-w-0 flex-1 mr-2">{q.query}</span>
                      <span className="shrink-0 text-muted-foreground">
                        {q.clicks}c / {q.impressions}i
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => refetchPage()}>
              {pageFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Refresh
            </Button>
          </div>
        )}

        {/* URL Inspection */}
        {!inspectData && !inspectFetching && (
          <Button variant="outline" size="sm" onClick={() => refetchInspect()} disabled={inspectFetching}>
            <Search className="mr-1.5 h-3.5 w-3.5" />
            Inspect URL
          </Button>
        )}
        {inspectFetching && !inspectData && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Inspecting URL...
          </div>
        )}
        {inspectData && (
          <div className="space-y-2 border-t pt-2">
            <p className="font-medium text-muted-foreground">URL Inspection</p>
            {inspectData.indexStatus && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground w-24 shrink-0">Index Status</span>
                  <Badge variant={
                    inspectData.indexStatus.verdict === 'PASS' ? 'outline' :
                    inspectData.indexStatus.verdict === 'NEUTRAL' ? 'secondary' : 'destructive'
                  } className={
                    inspectData.indexStatus.verdict === 'PASS' ? 'text-green-600 dark:text-green-400 border-green-600/30' :
                    inspectData.indexStatus.verdict === 'NEUTRAL' ? 'text-yellow-600 dark:text-yellow-400 border-yellow-600/30' :
                    'text-red-600 dark:text-red-400 border-red-600/30'
                  }>
                    {inspectData.indexStatus.verdict || 'Unknown'}
                  </Badge>
                </div>
                {inspectData.indexStatus.coverageState && (
                  <DetailRow label="Coverage">{inspectData.indexStatus.coverageState}</DetailRow>
                )}
                {inspectData.indexStatus.robotsTxtState && (
                  <DetailRow label="Robots.txt">{inspectData.indexStatus.robotsTxtState}</DetailRow>
                )}
                {inspectData.indexStatus.lastCrawlTime && (
                  <DetailRow label="Last Crawled">{new Date(inspectData.indexStatus.lastCrawlTime).toLocaleDateString('en-AU')}</DetailRow>
                )}
                {inspectData.indexStatus.googleCanonical && (
                  <DetailRow label="Google Canon.">
                    <span className="font-mono break-all">{inspectData.indexStatus.googleCanonical}</span>
                  </DetailRow>
                )}
                {inspectData.indexStatus.crawledAs && (
                  <DetailRow label="Crawled As">{inspectData.indexStatus.crawledAs}</DetailRow>
                )}
              </div>
            )}
            {inspectData.mobileUsability && (
              <div className="flex items-center gap-2">
                <span className="text-muted-foreground w-24 shrink-0">Mobile</span>
                <Badge variant={inspectData.mobileUsability.verdict === 'PASS' ? 'outline' : 'destructive'}
                  className={inspectData.mobileUsability.verdict === 'PASS' ? 'text-green-600 dark:text-green-400 border-green-600/30' : ''}>
                  {inspectData.mobileUsability.verdict || 'Unknown'}
                </Badge>
                {inspectData.mobileUsability.issues?.map((issue, i) => (
                  <span key={i} className="text-destructive">{issue.message || issue.issueType}</span>
                ))}
              </div>
            )}
            {inspectData.richResults && inspectData.richResults.detectedItems && inspectData.richResults.detectedItems.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-muted-foreground w-24 shrink-0">Rich Results</span>
                {inspectData.richResults.detectedItems.map((item, i) => (
                  <Badge key={i} variant="secondary">{item.richResultType}</Badge>
                ))}
              </div>
            )}
            <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => refetchInspect()}>
              {inspectFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Re-inspect
            </Button>
          </div>
        )}
      </div>
    </Section>
  )
}

function Ga4Section({ crawlId, pageUrl }: { crawlId: string; pageUrl: string }) {
  const { data: status } = useGa4Status(crawlId)
  const properties = status?.properties || []

  if (!status?.connected || properties.length === 0) return null

  const property = properties[0]!
  return <Ga4DataSection crawlId={crawlId} pageUrl={pageUrl} property={property} />
}

function Ga4DataSection({ crawlId, pageUrl, property }: { crawlId: string; pageUrl: string; property: Ga4PropertyMatch }) {
  const { data: pageData, isFetching, refetch } = useGa4Page(crawlId, property.propertyId, pageUrl)

  return (
    <Section title="Google Analytics" defaultOpen={false}>
      <div className="space-y-4 text-xs">
        <div className="flex items-center gap-2 text-muted-foreground mb-2">
          <BarChart3 className="h-3 w-3" />
          <span className="truncate">{property.displayName}</span>
          <span className="text-[10px]">({property.email})</span>
        </div>

        {!pageData && !isFetching && (
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isFetching}>
            <BarChart3 className="mr-1.5 h-3.5 w-3.5" />
            View Analytics
          </Button>
        )}
        {isFetching && !pageData && (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="h-3 w-3 animate-spin" />
            Loading analytics...
          </div>
        )}
        {pageData && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2">
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.pageViews.toLocaleString()}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <Eye className="h-3 w-3" /> Views
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.users.toLocaleString()}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <Users className="h-3 w-3" /> Users
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.sessions.toLocaleString()}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <Activity className="h-3 w-3" /> Sessions
                </p>
              </div>
              <div className="text-center">
                <p className="text-sm font-bold">{pageData.totals.bounceRate != null ? `${(pageData.totals.bounceRate * 100).toFixed(1)}%` : '—'}</p>
                <p className="text-muted-foreground leading-tight flex items-center justify-center gap-0.5">
                  <ArrowDownRight className="h-3 w-3" /> Bounce
                </p>
              </div>
            </div>

            {pageData.sources.length > 0 && (
              <div>
                <p className="font-medium text-muted-foreground mb-1">Traffic Sources</p>
                <div className="max-h-36 overflow-y-auto space-y-0.5">
                  {pageData.sources.slice(0, 8).map((s) => (
                    <div key={s.channel} className="flex items-center justify-between py-0.5">
                      <span className="flex items-center gap-1.5 truncate min-w-0 flex-1 mr-2">
                        <Globe className="h-3 w-3 text-muted-foreground shrink-0" />
                        {s.channel}
                      </span>
                      <span className="shrink-0 text-muted-foreground">
                        {s.sessions}s / {s.users}u
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <Button variant="ghost" size="sm" className="h-auto p-0 text-xs" onClick={() => refetch()}>
              {isFetching ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : null}
              Refresh
            </Button>
          </div>
        )}
      </div>
    </Section>
  )
}

export function PageDetailSheet({
  crawlId,
  pageId,
  onClose,
}: {
  crawlId: string
  pageId: string | null
  onClose: () => void
}) {
  const { data, isLoading, error } = usePageDetail(crawlId, pageId)

  return (
    <Sheet open={!!pageId} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="sm:max-w-2xl overflow-y-auto">
        {error ? (
          <SheetHeader>
            <SheetTitle>Error loading page</SheetTitle>
            <SheetDescription className="text-destructive">
              {(error as Error).message || 'Failed to load page details'}
            </SheetDescription>
          </SheetHeader>
        ) : isLoading || !data ? (
          <SheetHeader>
            <SheetTitle><Skeleton className="h-5 w-48" /></SheetTitle>
            <SheetDescription><Skeleton className="h-4 w-32" /></SheetDescription>
          </SheetHeader>
        ) : (
          <>
            <SheetHeader className="pb-0">
              <SheetTitle className="flex items-center gap-2 font-mono text-sm break-all pr-8">
                {data.page.url}
                <a
                  href={data.page.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="shrink-0 text-muted-foreground hover:text-foreground"
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </a>
              </SheetTitle>
              <SheetDescription asChild>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={data.page.status_code >= 400 ? 'destructive' : data.page.status_code >= 300 ? 'secondary' : 'outline'}
                  >
                    {data.page.status_code}
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    {data.page.content_type?.split(';')[0] || 'unknown'}
                  </span>
                  <SeoScoreBadge score={data.page.seo_score} size="sm" />
                  {data.page.status_code === 200 && (
                    data.page.is_indexable === 1 ? (
                      <Badge variant="outline" className="text-green-600 dark:text-green-400 border-green-600/30">Indexable</Badge>
                    ) : (
                      <Badge variant="outline" className="text-red-600 dark:text-red-400 border-red-600/30">Not Indexable</Badge>
                    )
                  )}
                </div>
              </SheetDescription>
            </SheetHeader>

            <div className="divide-y px-4 pb-4">
              {data.page.screenshot_key && (
                <ScreenshotSection crawlId={crawlId} page={data.page} />
              )}
              {data.page.ai_analysis
                ? <AiAnalysisSection page={data.page} />
                : <AiPageAuditSection page={data.page} />
              }
              <ReadableContentSection page={data.page} />
              <SeoEssentials page={data.page} />
              <Section title="SERP Preview">
                <SerpPreview page={data.page} />
              </Section>
              <AiSuggestionsSection crawlId={crawlId} pageId={data.page.id} />
              <Section title="Social Share Preview">
                <SocialPreview page={data.page} />
              </Section>
              <HeadingsSection page={data.page} />
              <InlinksSection inlinks={data.inlinks} />
              <OutlinksSection outlinks={data.outlinks} />
              <ImagesSection page={data.page} />
              <Section title="Structured Data">
                <SchemaValidation page={data.page} />
              </Section>
              <ResourcesSection page={data.page} />
              <RedirectChainSection page={data.page} />
              <ResponseHeadersSection page={data.page} />
              <ExtractionResultsSection page={data.page} />
              <SpellingsSection page={data.page} />
              <HreflangSection page={data.page} />
              <PageSpeedSection crawlId={crawlId} pageUrl={data.page.url} />
              <CruxSection crawlId={crawlId} pageUrl={data.page.url} />
              <GscSection crawlId={crawlId} pageUrl={data.page.url} />
              <Ga4Section crawlId={crawlId} pageUrl={data.page.url} />
              <TechnicalSection page={data.page} />
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  )
}
